import React, { useState, useEffect } from 'react';
import { Link as RouterLink, useNavigate, useLocation } from 'react-router-dom';
import { Eye, EyeOff, Mail, Lock } from 'lucide-react';
import { useAuth } from './AuthContext';
import { useAppTheme } from '../../contexts/ThemeContext';
import backgroundImage from './photo2.jpg';

const Login = () => {
  const navigate  = useNavigate();
  const location  = useLocation();
  const { login } = useAuth();
  const { palette, Colors, mode } = useAppTheme();

  const P     = Colors.primary.main;
  const Pdark = Colors.primary.dark;

  const [formData,     setFormData]     = useState({ email: '', password: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [error,        setError]        = useState('');
  const [info,         setInfo]         = useState('');
  const [loading,      setLoading]      = useState(false);

  useEffect(() => {
    if (location.state?.message) {
      setInfo(location.state.message);
      window.history.replaceState({}, document.title);
    }
  }, [location]);

  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.email || !formData.password) { setError('Please enter both email and password.'); return; }
    setError(''); setInfo(''); setLoading(true);
    try {
      await login({ email: formData.email, password: formData.password });
      navigate('/dashboard');
    } catch (err) {
      setError(err.message || 'Unable to sign in. Please try again.');
    } finally { setLoading(false); }
  };

  // ── Style helpers ─────────────────────────────────────────────
  const cardBg    = mode === 'dark' ? palette.background.secondary : 'rgba(255,255,255,0.95)';
  const cardBorder= mode === 'dark' ? palette.border               : 'rgba(255,255,255,0.2)';

  const inputStyle = (hasError = false) => ({
    backgroundColor: palette.background.secondary,
    color:           palette.text.primary,
    borderColor:     hasError ? Colors.status.error : palette.border,
    caretColor:      P,
  });

  const labelStyle  = { color: palette.text.secondary };
  const iconStyle   = { color: palette.text.tertiary };
  const dividerStyle= { borderColor: palette.border };

  const inputCls = "w-full py-3.5 border rounded-lg focus:outline-none focus:ring-2 transition text-base disabled:opacity-60";

  return (
    <div
      className="fixed inset-0 bg-cover bg-center bg-no-repeat flex items-center justify-center p-4"
      style={{ backgroundImage: `url(${backgroundImage})` }}
    >
      <div className="w-full max-w-xl">
        <div
          className="backdrop-blur-lg rounded-2xl shadow-2xl p-10"
          style={{ backgroundColor: cardBg, border: `1px solid ${cardBorder}` }}
        >
          {/* Header */}
          <div className="text-center mb-10">
            <h1 className="text-5xl font-bold mb-3" style={{
              background: `linear-gradient(to right, ${Pdark}, ${P})`,
              WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent'
            }}>
              Rental Management
            </h1>
            <p className="text-xl font-medium" style={{ color: palette.text.secondary }}>
              Welcome Back
            </p>
          </div>

          {/* Info banner */}
          {info && (
            <div className="mb-6 p-4 rounded-lg" style={{ backgroundColor: `${P}15`, border: `1px solid ${P}40` }}>
              <p className="text-sm" style={{ color: Pdark }}>{info}</p>
            </div>
          )}

          {/* Error banner */}
          {error && (
            <div className="mb-6 p-4 rounded-lg" style={{
              backgroundColor: `${Colors.status.error}12`,
              border: `1px solid ${Colors.status.error}40`
            }}>
              <p className="text-sm" style={{ color: Colors.status.error }}>{error}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Email */}
            <div>
              <label className="block text-sm font-medium mb-2" style={labelStyle}>Email Address</label>
              <div className="relative">
                <div className="absolute left-3 top-1/2 -translate-y-1/2" style={iconStyle}>
                  <Mail className="w-5 h-5" />
                </div>
                <input
                  type="email" name="email" value={formData.email} onChange={handleChange}
                  disabled={loading} autoFocus autoComplete="email" placeholder="you@example.com"
                  className={`${inputCls} pl-10 pr-4`}
                  style={inputStyle()}
                  onFocus={e => e.target.style.borderColor = P}
                  onBlur={e => e.target.style.borderColor  = palette.border}
                />
              </div>
            </div>

            {/* Password */}
            <div>
              <label className="block text-sm font-medium mb-2" style={labelStyle}>Password</label>
              <div className="relative">
                <div className="absolute left-3 top-1/2 -translate-y-1/2" style={iconStyle}>
                  <Lock className="w-5 h-5" />
                </div>
                <input
                  type={showPassword ? 'text' : 'password'} name="password" value={formData.password}
                  onChange={handleChange} disabled={loading} autoComplete="current-password"
                  placeholder="Enter your password"
                  className={`${inputCls} pl-10 pr-12`}
                  style={inputStyle()}
                  onFocus={e => e.target.style.borderColor = P}
                  onBlur={e => e.target.style.borderColor  = palette.border}
                />
                <button
                  type="button" onClick={() => setShowPassword(!showPassword)} disabled={loading}
                  className="absolute right-3 top-1/2 -translate-y-1/2 transition"
                  style={iconStyle}
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            {/* Submit */}
            <button
              type="submit" disabled={loading}
              className="w-full py-4 px-4 text-white font-semibold rounded-lg shadow-lg transform transition hover:scale-[1.02] active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none text-lg mt-8"
              style={{ background: loading ? P : `linear-gradient(to right, ${Pdark}, ${P})` }}
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Signing In...
                </span>
              ) : 'Sign In'}
            </button>

            {/* Links */}
            <div className="flex items-center justify-between mt-8 text-sm">
              <RouterLink to="/forgot-password" className="font-medium hover:underline" style={{ color: P }}>
                Forgot password?
              </RouterLink>
              <RouterLink to="/register" className="font-medium hover:underline" style={{ color: P }}>
                Create account
              </RouterLink>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Login;